import { SettingsPermissions } from "@redotech/account-common/permissions";
import { directMerchantAccess } from "../../settings-catalog/access";
import type {
  SettingGroupDefinition,
  SettingOwner,
  SettingRisk,
} from "../../settings-catalog/types";

export type InventoryPurchaseOrdersFlatTeamSettingGroupId =
  | "inventory_purchase_orders.purchase_orders"
  | "inventory_purchase_orders.recommendation_urgencies";

const groupOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.ims",
  product: "fulfillment",
};

const groupRisk: SettingRisk = {
  level: "low",
  reason:
    "Defaults applied to new purchase orders and the urgency levels shown in purchasing recommendations.",
};

export const InventoryPurchaseOrdersFlatTeamSettingGroups = [
  {
    id: "inventory_purchase_orders.purchase_orders",
    title: "Purchase orders",
    description:
      "Defaults applied to every new purchase order: the buyer address preselected on the order, and the emails automatically copied when a purchase order is sent to a supplier.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "inventory-management/purchase-orders",
    },
    synonyms: ["purchase orders", "inventory purchase orders"],
  },
  {
    id: "inventory_purchase_orders.recommendation_urgencies",
    title: "Recommendation urgencies",
    description:
      "Which urgency levels appear in the Forecasting purchasing recommendations list, so buyers see only the urgencies they act on.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "inventory-management/purchase-orders",
    },
    synonyms: ["recommendation urgencies", "inventory purchase orders"],
  },
] as const satisfies readonly SettingGroupDefinition<InventoryPurchaseOrdersFlatTeamSettingGroupId>[];

type _assertAllInventoryPurchaseOrdersFlatTeamSettingGroupIdsProvided =
  InventoryPurchaseOrdersFlatTeamSettingGroupId extends (typeof InventoryPurchaseOrdersFlatTeamSettingGroups)[number]["id"]
    ? true
    : never;
const _allInventoryPurchaseOrdersFlatTeamSettingGroupIdsProvided: _assertAllInventoryPurchaseOrdersFlatTeamSettingGroupIdsProvided = true;
void _allInventoryPurchaseOrdersFlatTeamSettingGroupIdsProvided;

export const InventoryPurchaseOrdersFlatTeamSettingGroupById: Record<
  InventoryPurchaseOrdersFlatTeamSettingGroupId,
  SettingGroupDefinition<InventoryPurchaseOrdersFlatTeamSettingGroupId>
> = (() => {
  const map: Partial<
    Record<
      InventoryPurchaseOrdersFlatTeamSettingGroupId,
      SettingGroupDefinition<InventoryPurchaseOrdersFlatTeamSettingGroupId>
    >
  > = {};
  for (const group of InventoryPurchaseOrdersFlatTeamSettingGroups) {
    map[group.id] = group;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return map as Record<
    InventoryPurchaseOrdersFlatTeamSettingGroupId,
    SettingGroupDefinition<InventoryPurchaseOrdersFlatTeamSettingGroupId>
  >;
})();
